import React, { useEffect, useState, useRef } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../firebase/AuthContext";
import { auth } from "../firebase/firebase";
import { signOut } from "firebase/auth";
import { FiBarChart2, FiChevronDown, FiUser } from "react-icons/fi";
import { useTheme } from "../hooks/useTheme";
import MyUsageModal from "./usageBilling/MyUsageModal";
import dashboardIcon from "../assets/svg/dashboard.svg";
import aiIcon from "../assets/svg/ai.svg";
import aichatIcon from "../assets/svg/aichat.svg";
import historyIcon from "../assets/svg/history.svg";
import updateIcon from "../assets/svg/update.svg";
import competitorIcon from "../assets/svg/competitor.svg";
import feedbackIcon from "../assets/svg/feedback.svg";
import adminIcon from "../assets/svg/admin.svg";
import avatar from "../assets/usericon.png";
import DocsIcon from "../assets/svg/about.svg";
import logo from "../assets/logo_tglobal.png";
import logoDark from "../assets/logo_tglobal_white.png";
import dashboardIconDark from "../assets/svg/dashboard_white.svg";
import aiIconDark from "../assets/svg/ai_white.svg";
import aichatIconDark from "../assets/svg/aichat_white.svg";
import historyIconDark from "../assets/svg/history_white.svg";
import updateIconDark from "../assets/svg/update_white.svg";
import competitorIconDark from "../assets/svg/competitor_white.svg";
import signoutIconDark from "../assets/svg/signout_white.svg";
import feedbackIconDark from "../assets/svg/feedback_white.svg";
import adminIconDark from "../assets/svg/admin_white.svg";
import DocsIconDark from "../assets/svg/about_white.svg";
import apiIconDark from "../assets/svg/api_white.svg";
import botPlatform from "../assets/svg/bot_platform.svg";
import botPlatformDark from "../assets/svg/bot_platform_white.svg";
import userFeedbackIcon from "../assets/svg/userFeedback.svg";
import UserFeedbackIconDark from "../assets/svg/userFeedback_white.svg";
import faqIcon from "../assets/svg/faq.svg";
import faqIconDark from "../assets/svg/faq_white.svg";
import apiIcon from "../assets/svg/api.svg";
import faqchat from "../assets/svg/faq_chatbot.svg";
import faqchatDark from "../assets/svg/faq_chatbot_white.svg";
import newchatIcon from "../assets/svg/newchat.svg";
import newchatIconDark from "../assets/svg/newchat_white.svg";

function Sidebar() {
  const { getUserProfile, hasRole } = useAuth();
  const profile = getUserProfile();
  const navigate = useNavigate();
  const location = useLocation();
  const { theme, toggleTheme } = useTheme();
  const [signOutModal, setSignOutModal] = useState(false);
  // Persisted across page navigation/reload so a freshly-mounted Sidebar
  // (each page renders its own) keeps the pinned/expanded state.
  const [isExpanded, setIsExpanded] = useState(
    () => localStorage.getItem("sidebarExpanded") === "true",
  );
  const [isPinned, setIsPinned] = useState(
    () => localStorage.getItem("sidebarPinned") === "true",
  );
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [aiAssistantOpen, setAiAssistantOpen] = useState(true);
  // The profile button opens a small menu: the profile page, or the usage modal.
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [usageOpen, setUsageOpen] = useState(false);
  const profileMenuRef = useRef(null);
  const hoverTimeoutRef = useRef(null);

  const getLangFromUrl = () => {
    const params = new URLSearchParams(location.search);
    return params.get("lang") === "zh" ? "zh" : "en";
  };

  const [language, setLanguage] = useState(getLangFromUrl());
  const [isMobile, setIsMobile] = useState(false);

  // Check if mobile on mount and resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Persist pinned/expanded state so it survives page navigation and reload
  useEffect(() => {
    localStorage.setItem("sidebarPinned", isPinned);
  }, [isPinned]);

  useEffect(() => {
    localStorage.setItem("sidebarExpanded", isExpanded);
  }, [isExpanded]);

  // Keep language in sync with URL (if user pastes a new URL)
  useEffect(() => {
    setLanguage(getLangFromUrl());
    // eslint-disable-next-line
  }, [location.search]);

  // Close mobile menu when route changes
  useEffect(() => {
    setIsMobileMenuOpen(false);
    setProfileMenuOpen(false);
  }, [location.pathname]);

  // The profile menu closes on a click outside it, on Escape, and when the
  // sidebar collapses under it.
  useEffect(() => {
    if (!profileMenuOpen) return;
    const onMouseDown = (e) => {
      if (!profileMenuRef.current?.contains(e.target)) {
        setProfileMenuOpen(false);
      }
    };
    const onKeyDown = (e) => {
      if (e.key === "Escape") setProfileMenuOpen(false);
    };
    document.addEventListener("mousedown", onMouseDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onMouseDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [profileMenuOpen]);

  useEffect(() => {
    if (!isExpanded) setProfileMenuOpen(false);
  }, [isExpanded]);

  // Auto-open AI Assistant dropdown when on a child route
  useEffect(() => {
    if (
      location.pathname === "/ai-chat" ||
      location.pathname === "/faq/ai-chat" ||
      location.pathname === "/tg-product-assistant" ||
      location.pathname === "/history"
    ) {
      setAiAssistantOpen(true);
    }
  }, [location.pathname]);

  const handleSignOut = async () => {
    try {
      // Clear chat state on sign out
      clearChatState();
      await signOut(auth);
      navigate({ pathname: "/", search: `?lang=${language}` });
    } catch (error) {
      console.error("Sign-out error:", error);
    }
  };

  const clearChatState = () => {
    const user = auth.currentUser;
    if (!user) return;

    localStorage.removeItem(`currentSessionId_${user.uid}`);
    localStorage.removeItem(`currentMessages_${user.uid}`);
  };

  // const handleMouseEnter = () => {
  //     // Clear any existing timeout
  //     if (hoverTimeoutRef.current) {
  //         clearTimeout(hoverTimeoutRef.current);
  //     }
  //     setIsExpanded(true);
  // };

  // const handleMouseLeave = () => {
  //     // Add a small delay before collapsing to prevent flicker
  //     hoverTimeoutRef.current = setTimeout(() => {
  //         setIsExpanded(false);
  //     }, 100);
  // };

  // Cleanup timeout on unmount
  useEffect(() => {
    // Capture the ref now: `hoverTimeoutRef.current` may point elsewhere by
    // the time this cleanup runs.
    const timeoutRef = hoverTimeoutRef;
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  if (!profile) {
    // This should be handled by parent component
    return null;
  }

  const trendReportSvg = (
    <svg
      className="w-6 h-6 flex-shrink-0"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
      />
    </svg>
  );

  // Define navigation items with theme-aware icons
  const mainNavItems = [
    {
      path: "/dashboard",
      icon: theme === "dark" ? dashboardIconDark : dashboardIcon,
      label: { en: "Dashboard", zh: "儀表板" },
    },
    {
      // AI Assistant dropdown group — handled separately in JSX
      id: "ai-assistant",
      icon: theme === "dark" ? aiIconDark : aiIcon,
      label: { en: "AI Assistant", zh: "AI 助手" },
      children: [
        {
          path: "/tg-product-assistant",
          icon: theme === "dark" ? newchatIconDark : newchatIcon,
          label: {
            en: "TG PIMS Agent",
            zh: "TG PIMS Agent",
          },
        },
        {
          // Supplementary agents, listed under their own small heading
          id: "other-assistants",
          label: { en: "Other assistants", zh: "其他助手" },
          children: [
            {
              path: "/ai-chat",
              icon: theme === "dark" ? aichatIconDark : aichatIcon,
              label: { en: "PIMS Chat", zh: "PIMS 聊天" },
            },
            {
              path: "/faq/ai-chat",
              icon: theme === "dark" ? faqchatDark : faqchat,
              label: { en: "FAQ & Tech Q&A", zh: "FAQ 聊天機器人" },
            },
          ],
        },
        ...(hasRole && hasRole("assistant.history")
          ? [
              {
                path: "/history",
                icon: theme === "dark" ? historyIconDark : historyIcon,
                label: { en: "History", zh: "歷史紀錄" },
              },
            ]
          : []),
      ],
    },
    ...(hasRole && hasRole("trend.read")
      ? [
          {
            path: "/trend-reports",
            iconSvg: trendReportSvg,
            label: { en: "Trend Reports", zh: "趨勢報告" },
          },
        ]
      : []),
    ...(hasRole && hasRole("product.read")
      ? [
          {
            path: "/product",
            icon: theme === "dark" ? updateIconDark : updateIcon,
            label: { en: "Product Management", zh: "產品管理" },
          },
        ]
      : []),
    ...(hasRole && hasRole("competitor.read")
      ? [
          {
            path: "/product-competitor",
            icon: theme === "dark" ? competitorIconDark : competitorIcon,
            label: { en: "Competitor Insights", zh: "競爭對手洞察" },
          },
        ]
      : []),
  ];

  const utilityNavItems = [
    ...(hasRole && hasRole(["api.view_own", "api.view_all"])
      ? [
          {
            path: "/api",
            icon: theme === "dark" ? apiIconDark : apiIcon,
            label: { en: "API", zh: "API" },
          },
        ]
      : []),
    ...(hasRole && hasRole(["webchatbot.view_own", "webchatbot.view_all"])
      ? [
          {
            path: "/bot-platform/dashboard",
            icon: theme === "dark" ? botPlatformDark : botPlatform,
            label: { en: "Bot Platform", zh: "Bot 平台" },
          },
        ]
      : []),
    ...(hasRole && hasRole("knowledge.faq.review")
      ? [
          {
            path: "/faq",
            icon: theme === "dark" ? faqIconDark : faqIcon,
            label: { en: "FAQ Management", zh: "FAQ 管理" },
          },
        ]
      : []),
    ...(hasRole && hasRole("admin_panel.access")
      ? [
          {
            path: "/admin",
            icon: theme === "dark" ? adminIconDark : adminIcon,
            label: { en: "Admin", zh: "權限管理" },
          },
        ]
      : []),
    ...(hasRole && hasRole("assistant.feedback")
      ? [
          {
            path: "/feedback",
            icon: theme === "dark" ? feedbackIconDark : feedbackIcon,
            label: { en: "Feedback", zh: "回饋" },
          },
        ]
      : []),
    ...(hasRole && hasRole("feedback.read")
      ? [
          {
            path: "/user-feedback",
            icon: theme === "dark" ? UserFeedbackIconDark : userFeedbackIcon,
            label: { en: "Feedback Hub", zh: "回饋中心" },
          },
        ]
      : []),
    {
      path: "/docs",
      icon: theme === "dark" ? DocsIconDark : DocsIcon,
      label: { en: "Docs", zh: "文件" },
    },
  ];

  // Helper to create nav button classes. `small` rows (the Other assistants
  // group) are shorter and tighter.
  const getNavButtonClass = (isActive, small = false) => {
    const baseClass = `w-full flex items-center ${
      small ? "gap-2 px-2 py-2" : "gap-3 px-3 py-3"
    } rounded-lg transition-all duration-300 ease-out focus:outline-none`;
    const activeClass = isActive
      ? "bg-gray-300 dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm"
      : "text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-gray-100";
    const layoutClass =
      isMobile || isExpanded ? "justify-start" : "justify-center";
    return `${baseClass} ${activeClass} ${layoutClass}`;
  };

  // Mobile hamburger menu component
  const MobileMenuButton = () => (
    <button
      onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
      className="fixed top-4 left-4 z-50 p-2 bg-white rounded-lg shadow-lg hover:bg-gray-50 transition-colors duration-200 focus:outline-none md:hidden"
      aria-label="Toggle menu"
    >
      <svg
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className={`transform transition-transform duration-300 ${
          isMobileMenuOpen ? "rotate-45" : "rotate-0"
        }`}
      >
        {isMobileMenuOpen ? (
          <path
            d="M18 6L6 18M6 6L18 18"
            stroke="#374151"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        ) : (
          <path
            d="M3 12H21M3 6H21M3 18H21"
            stroke="#374151"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        )}
      </svg>
    </button>
  );

  return (
    <>
      {/* Mobile menu button */}
      <MobileMenuButton />

      {/* Mobile backdrop */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 md:hidden"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        onMouseEnter={() => !isMobile && !isPinned && setIsExpanded(true)}
        onMouseLeave={() => !isMobile && !isPinned && setIsExpanded(false)}
        className={`
                    bg-sidebar-bg dark:bg-gray-800 py-6 px-4 flex flex-col min-h-0 transition-all duration-400 ease-in-out border-r border-gray-200 dark:border-gray-700
                    ${
                      // Desktop behavior
                      !isMobile ? (isExpanded ? "w-64" : "w-24") : ""
                    }
                    ${
                      // Mobile behavior - hidden by default, overlay when open
                      isMobile
                        ? `fixed top-0 left-0 h-full w-64 z-50 transform ${
                            isMobileMenuOpen
                              ? "translate-x-0"
                              : "-translate-x-full"
                          }`
                        : "sticky top-0 h-screen"
                    }
                `}
      >
        {/* Header with logo and toggle */}
        <div className="flex items-center justify-between mb-6">
          <div
            className={`flex items-center transition-all duration-300 ${
              isMobile || isExpanded ? "gap-3" : "justify-center w-full"
            }`}
            onClick={() =>
              navigate({
                pathname: "/",
                search: `?lang=${language}`,
              })
            }
          >
            <img
              src={theme === "dark" ? logoDark : logo}
              alt="T-Global"
              className={`transition-all duration-300 ${
                isMobile || isExpanded ? "w-32" : "w-8"
              }`}
            />
          </div>
          {!isMobile && (
            <button
              onClick={() => {
                setIsExpanded(!isExpanded);
              }}
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200 focus:outline-none"
              title={isExpanded ? "Collapse sidebar" : "Expand sidebar"}
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className={`transform transition-transform duration-300 ${
                  isExpanded ? "rotate-0" : "rotate-180"
                }`}
              >
                <path
                  d="M15.5 19L8.5 12L15.5 5"
                  stroke="currentColor"
                  className="text-gray-500 dark:text-white"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          )}
        </div>
        {/* Profile Section: opens a menu - the profile page, or the usage modal.
            The menu sits inside the aside so moving onto it keeps a
            hover-expanded sidebar open. */}
        <div className="mb-6 relative" ref={profileMenuRef}>
          <button
            onClick={() => setProfileMenuOpen((open) => !open)}
            aria-haspopup="menu"
            aria-expanded={profileMenuOpen}
            className={`w-full flex items-center gap-3 py-3 px-3 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200 focus:outline-none ${
              location.pathname === "/edit-profile" || profileMenuOpen
                ? "bg-gray-200 dark:bg-gray-700"
                : ""
            } ${isMobile || isExpanded ? "justify-start" : "justify-center"}`}
          >
            <img
              src={profile.photoURL ? profile.photoURL : avatar}
              alt="Profile"
              className={`rounded-full object-cover transition-all duration-300 ${
                isMobile || isExpanded ? "w-10 h-10" : "w-8 h-8"
              }`}
            />
            {(isMobile || isExpanded) && (
              <>
                <div className="flex-1 text-left overflow-hidden">
                  <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
                    {profile.displayName || "User"}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-100 truncate">
                    {profile.email}
                  </div>
                </div>
                <FiChevronDown
                  className={`w-4 h-4 flex-shrink-0 text-gray-400 transition-transform duration-200 ${
                    profileMenuOpen ? "rotate-180" : "rotate-0"
                  }`}
                  aria-hidden="true"
                />
              </>
            )}
          </button>

          {profileMenuOpen && (
            <div
              role="menu"
              className="absolute left-0 top-full mt-1 z-20 w-56 py-1 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-lg animate-fadeIn"
            >
              <button
                role="menuitem"
                onClick={() => {
                  setProfileMenuOpen(false);
                  navigate({
                    pathname: "/edit-profile",
                    search: `?lang=${language}`,
                  });
                }}
                className="w-full flex items-center gap-3 px-3 py-2 text-sm text-left text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <FiUser className="w-4 h-4 flex-shrink-0" aria-hidden="true" />
                {language === "en" ? "Profile" : "個人資料"}
              </button>
              <button
                role="menuitem"
                onClick={() => {
                  setProfileMenuOpen(false);
                  setIsMobileMenuOpen(false);
                  setUsageOpen(true);
                }}
                className="w-full flex items-center gap-3 px-3 py-2 text-sm text-left text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <FiBarChart2
                  className="w-4 h-4 flex-shrink-0"
                  aria-hidden="true"
                />
                {language === "en" ? "My AI usage" : "我的 AI 用量"}
              </button>
            </div>
          )}
        </div>
        {/* Navigation */}
        <nav className="flex-1 min-h-0 mb-6 overflow-y-auto sidebar-scroll">
          {/* Main section */}
          {(isMobile || isExpanded) && (
            <p className="px-3 mb-1 text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">
              {language === "en" ? "Main" : "主要"}
            </p>
          )}
          <ul className={`space-y-1 mb-4 ${isExpanded ? "pr-2" : ""}`}>
            {mainNavItems.map((item) => {
              if (item.children) {
                // Dropdown group (AI Assistant)
                const isChildActive = item.children.some(
                  (c) =>
                    location.pathname === c.path ||
                    c.children?.some((s) => location.pathname === s.path),
                );
                const showExpanded = isMobile || isExpanded;
                return (
                  <li key={item.id}>
                    {/* Group header */}
                    <button
                      onClick={() => {
                        if (showExpanded) {
                          setAiAssistantOpen((o) => !o);
                        } else {
                          // When sidebar is collapsed, navigate straight to chat
                          navigate({
                            pathname: "/tg-product-assistant",
                            search: `?lang=${language}`,
                          });
                        }
                      }}
                      className={getNavButtonClass(isChildActive)}
                      title={!showExpanded ? item.label[language] : undefined}
                    >
                      <img
                        src={item.icon}
                        alt=""
                        className="w-6 h-6 flex-shrink-0"
                      />
                      {showExpanded && (
                        <>
                          <span className="flex-1 text-sm font-medium truncate text-dark-700 dark:text-gray-100 text-left">
                            {item.label[language]}
                          </span>
                          <svg
                            className={`w-4 h-4 flex-shrink-0 text-gray-400 transition-transform duration-200 ${
                              aiAssistantOpen ? "rotate-180" : "rotate-0"
                            }`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 9l-7 7-7-7"
                            />
                          </svg>
                        </>
                      )}
                    </button>
                    {/* Children */}
                    {showExpanded && aiAssistantOpen && (
                      <ul className="mt-1 ml-4 space-y-1 border-l-2 border-gray-400 dark:border-gray-500 pl-3">
                        {item.children.map((child) => {
                          if (child.children) {
                            // Labelled group (Other assistants): smaller rows
                            return (
                              <li key={child.id} className="pt-2">
                                <p className="px-3 mb-1 text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">
                                  {child.label[language]}
                                </p>
                                <ul className="ml-3 space-y-1 border-l-2 border-gray-300 dark:border-gray-600 pl-2">
                                  {child.children.map((sub) => (
                                    <li key={sub.path}>
                                      <button
                                        onClick={() =>
                                          navigate({
                                            pathname: sub.path,
                                            search: `?lang=${language}`,
                                          })
                                        }
                                        className={getNavButtonClass(
                                          location.pathname === sub.path,
                                          true,
                                        )}
                                      >
                                        <img
                                          src={sub.icon}
                                          alt=""
                                          className="w-4 h-4 flex-shrink-0"
                                        />
                                        <span className="text-[13px] font-medium truncate text-dark-700 dark:text-gray-100">
                                          {sub.label[language]}
                                        </span>
                                      </button>
                                    </li>
                                  ))}
                                </ul>
                              </li>
                            );
                          }

                          const isActive = location.pathname === child.path;
                          return (
                            <li key={child.path}>
                              <button
                                onClick={() =>
                                  navigate({
                                    pathname: child.path,
                                    search: `?lang=${language}`,
                                  })
                                }
                                className={getNavButtonClass(isActive)}
                              >
                                {child.iconSvg ? (
                                  child.iconSvg
                                ) : (
                                  <img
                                    src={child.icon}
                                    alt=""
                                    className="w-5 h-5 flex-shrink-0"
                                  />
                                )}
                                <span className="text-sm font-medium truncate text-dark-700 dark:text-gray-100">
                                  {child.label[language]}
                                </span>
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </li>
                );
              }

              // Regular nav item
              const isActive = location.pathname === item.path;
              return (
                <li key={item.path}>
                  <button
                    onClick={() =>
                      navigate({
                        pathname: item.path,
                        search: `?lang=${language}`,
                      })
                    }
                    className={getNavButtonClass(isActive)}
                    title={
                      !isMobile && !isExpanded
                        ? item.label[language]
                        : undefined
                    }
                  >
                    {item.iconSvg ? (
                      item.iconSvg
                    ) : (
                      <img
                        src={item.icon}
                        alt=""
                        className="w-6 h-6 flex-shrink-0"
                      />
                    )}
                    {(isMobile || isExpanded) && (
                      <span className="text-sm font-medium truncate text-dark-700 dark:text-gray-100">
                        {item.label[language]}
                      </span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>

          {/* Divider */}
          <div className="border-t border-gray-400 dark:border-gray-500 mb-4" />

          {/* Utilities section */}
          {(isMobile || isExpanded) && (
            <p className="px-3 mb-1 text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">
              {language === "en" ? "Utilities" : "工ss具"}
            </p>
          )}
          <ul className={`space-y-1 mb-4 ${isExpanded ? "pr-2" : ""}`}>
            {utilityNavItems.map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <li key={item.path}>
                  <button
                    onClick={() =>
                      navigate({
                        pathname: item.path,
                        search: `?lang=${language}`,
                      })
                    }
                    className={getNavButtonClass(isActive)}
                    title={
                      !isMobile && !isExpanded
                        ? item.label[language]
                        : undefined
                    }
                  >
                    {item.iconSvg ? (
                      item.iconSvg
                    ) : (
                      <img
                        src={item.icon}
                        alt=""
                        className="w-6 h-6 flex-shrink-0"
                      />
                    )}
                    {(isMobile || isExpanded) && (
                      <span className="text-sm font-medium truncate text-dark-700 dark:text-gray-100">
                        {item.label[language]}
                      </span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* Theme Toggle Switch */}
        <div className="mb-3 flex flex-row items-center justify-center gap-2">
          {!isMobile && isExpanded && (
            <button
              onClick={() => {
                if (isPinned) {
                  // Second click: back to default hover-based open/close
                  setIsPinned(false);
                } else {
                  // First click: keep the sidebar stuck open
                  setIsPinned(true);
                  setIsExpanded(true);
                }
              }}
              className={`flex items-center justify-center py-3 px-3 rounded-lg transition-colors duration-200 focus:outline-none ${
                isPinned
                  ? "bg-orange-500 text-white hover:bg-orange-600"
                  : "bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600"
              }`}
              title={isPinned ? "Unpin sidebar" : "Keep sidebar open"}
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                {isPinned ? (
                  // Locked (pinned open)
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                  />
                ) : (
                  // Unlocked (default hover behavior)
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 11V7a4 4 0 018 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z"
                  />
                )}
              </svg>
            </button>
          )}
          <div
            className={`flex items-center gap-3 py-3 px-3 rounded-lg bg-gray-100 dark:bg-gray-700 ${
              isMobile || isExpanded
                ? "justify-between w-full"
                : "justify-center flex-1"
            }`}
          >
            {(isMobile || isExpanded) && (
              <div className="flex items-center gap-2 text-gray-700 dark:text-gray-200">
                {theme === "dark" ? (
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"
                    />
                  </svg>
                ) : (
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"
                    />
                  </svg>
                )}
                <span className="text-sm font-medium">
                  {language === "en" ? "Theme" : "主題"}
                </span>
              </div>
            )}

            <button
              onClick={toggleTheme}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 dark:focus:ring-offset-gray-700 ${
                theme === "dark"
                  ? "bg-orange-500"
                  : "bg-gray-300 dark:bg-gray-600"
              }`}
              title={
                !isMobile && !isExpanded
                  ? theme === "dark"
                    ? language === "en"
                      ? "Switch to Light Mode"
                      : "切換至淺色模式"
                    : language === "en"
                      ? "Switch to Dark Mode"
                      : "切換至深色模式"
                  : undefined
              }
            >
              <span
                className={`inline-flex h-5 w-5 items-center justify-center transform rounded-full bg-white shadow-lg transition-transform duration-300 ${
                  theme === "dark" && isExpanded
                    ? "translate-x-5"
                    : "translate-x-0.5"
                }`}
              >
                {theme === "dark" ? (
                  // Sun icon (small)
                  <svg
                    className="w-3 h-3 text-gray-600"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z" />
                  </svg>
                ) : (
                  // Moon icon (small)

                  <svg
                    className="w-3 h-3 text-orange-500"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z"
                      clipRule="evenodd"
                    />
                  </svg>
                )}
              </span>
            </button>
          </div>
        </div>

        {/* Sign out button */}
        <button
          onClick={() => setSignOutModal(true)}
          className={`w-full flex items-center gap-3 py-3 px-3 rounded-lg bg-orange-500 hover:bg-orange-600 text-white transition-colors duration-200 focus:outline-none ${
            isMobile || isExpanded ? "justify-start" : "justify-center"
          }`}
          title={
            !isMobile && !isExpanded
              ? language === "en"
                ? "Sign Out"
                : "登出"
              : undefined
          }
        >
          <img
            src={theme === "dark" ? signoutIconDark : signoutIconDark}
            alt=""
            className="w-5 h-5"
          />
          {(isMobile || isExpanded) && (
            <span className="text-sm font-medium">
              {language === "en" ? "Sign Out" : "登出"}
            </span>
          )}
        </button>
      </aside>

      {/* Outside the aside, so the pointer over the modal isn't "in" the
          sidebar and a hover-expanded sidebar collapses as usual. */}
      {usageOpen && (
        <MyUsageModal onClose={() => setUsageOpen(false)} language={language} />
      )}

      {/* Sign out modal — portaled to escape the sidebar's stacking context */}
      {signOutModal &&
        createPortal(
          <div
            className="fixed inset-0 bg-black/40 backdrop-blur-sm transition-opacity flex items-center justify-center z-50 animate-fadeIn"
            onClick={() => setSignOutModal(false)}
          >
            <div
              className="bg-white dark:bg-gray-800 rounded-xl p-6 max-w-sm w-full mx-4 shadow-xl animate-slideIn"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center mb-4">
                <div className="w-10 h-10 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mr-3">
                  <svg
                    className="w-5 h-5 text-red-600 dark:text-red-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"
                    />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  {language === "en" ? "Confirm Sign Out" : "確認登出"}
                </h3>
              </div>

              <p className="text-gray-600 dark:text-gray-400 mb-6 text-sm">
                {language === "en"
                  ? "Are you sure you want to sign out?"
                  : "您確定要登出嗎？"}
              </p>

              <div className="flex gap-3 justify-end">
                <button
                  className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors duration-200 font-medium text-sm"
                  onClick={() => setSignOutModal(false)}
                >
                  {language === "en" ? "Cancel" : "取消"}
                </button>
                <button
                  className="px-4 py-2 text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors duration-200 font-medium text-sm"
                  onClick={handleSignOut}
                >
                  {language === "en" ? "Sign Out" : "登出"}
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}

export default Sidebar;
